import React from "react"
import { Label, Popup, List } from "semantic-ui-react"
import ActivatedHeaderCheckbox from "../components/vehicles/ActivatedHeaderCheckbox"
import { isFleetMaintenanceHubCanada, isFuelman } from "../helpers/affiliationHelpers"
import * as numberHelpers from "../helpers/numberHelpers"
import * as vehicleHelpers from "../helpers/vehicleHelpers"

const CPK = "CPK"
const CPM = "CPM"

export const MANUALLY = "Manually"
export const NO = "No"
export const OBJECT = "object"
export const ACTIVE = "active"
export const DEACTIVE = "deactive"

const driversCellRenderer = (defaultCellRenderer) => (cellParams) => {
  const drivers = [...(cellParams.data?.driver_names_list || [])].sort((a, b) => {
    const nameA = typeof a === "string" ? a : a?.name || a?.first_name || ""
    const nameB = typeof b === "string" ? b : b?.name || b?.first_name || ""
    return nameA.localeCompare(nameB)
  })

  let content = cellParams.value
  if (Array.isArray(drivers) && drivers.length > 1) {
    const firstDriver =
      typeof drivers[0] === "string" ? drivers[0] : drivers[0]?.name || drivers[0]?.first_name || ""
    const restDrivers = drivers
      .slice(1)
      .map((d) => (typeof d === "string" ? d : d?.name || d?.first_name || "Unknown"))

    content = (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {firstDriver}
        </span>
        <Popup
          trigger={
            <Label
              size="large"
              style={{ marginLeft: "8px", cursor: "pointer" }}
              onClick={(e) => e.preventDefault()}
            >
              +{drivers.length - 1}
            </Label>
          }
          on="click"
          position="bottom right"
          basic
          wide="very"
          positionFixed
          popperModifiers={{ computeStyle: { gpuAcceleration: false } }}
        >
          <p>All Drivers ({drivers.length})</p>
          <div
            style={
              drivers.length > 5
                ? { maxHeight: "150px", overflowY: "auto", paddingRight: "10px" }
                : {}
            }
          >
            <List divided relaxed size="large">
              {restDrivers.map((driverName, index) => (
                <List.Item key={index}>{driverName}</List.Item>
              ))}
            </List>
          </div>
        </Popup>
      </div>
    )
  }
  return defaultCellRenderer({ ...cellParams, value: content })
}

export function agGridColumnDefs(
  defaultCellRenderer,
  editCellRenderer,
  deleteCellRenderer,
  deactivateCellRenderer,
  callToActionRenderer,
  t,
  isFleetcor,
  vehicles,
  checkboxBulkRender,
  handleBulkCheckBox,
  activateRootCheckbox
) {
  const widths = {
    extraSmall: 50,
    verySmall: 125,
    small: 150,
    medium: 175,
    large: 200,
    veryLarge: 225
  }
  var fields = []

  fields = [
    {
      field: "bulk_activation",
      width: widths.extraSmall,
      cellRendererFramework: checkboxBulkRender,
      headerComponentFramework: ActivatedHeaderCheckbox,
      headerComponentParams: { handleBulkCheckBox, checked: activateRootCheckbox },
      suppressMenu: true,
      suppressSorting: true
    }
  ]

  if (isFleetcor)
    fields = [
      ...fields,
      {
        headerName: "Enrolled",
        field: "deactivate_button",
        width: widths.medium,
        cellRendererFramework: deactivateCellRenderer,
        suppressMenu: true,
        suppressSorting: true,
        sortable: false
      },
      {
        headerName: t("vehicle:vehicleNameLabel"),
        field: "name_of_vehicle",
        width: widths.veryLarge,
        cellRendererFramework: defaultCellRenderer
      }
    ]

  fields = [
    ...fields,
    {
      headerName: t("vehicleLabel"),
      field: "vehicle_name",
      width: widths.veryLarge,
      cellRendererFramework: callToActionRenderer
    },
    {
      headerName: t("otherIdLabel"),
      field: "other_id",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },

    {
      headerName: t("dashboard:DepartmentNumber"),
      field: "department_number",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("vehicle:vinLabel"),
      field: "vin",
      width: widths.veryLarge,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("vehicle:licensePlate"),
      field: "license_plate_number",
      width: widths.large,
      cellRendererFramework: defaultCellRenderer,
      sortable: false
    },
    {
      headerName: t("driversLabel"),
      field: "driver_name",
      width: widths.veryLarge,
      cellRendererFramework: driversCellRenderer(defaultCellRenderer),
      sortable: false
    },
    {
      headerName: isFleetMaintenanceHubCanada()
        ? t("dashboard:kilometersLabel")
        : t("dashboard:mileageHeadingLabel"),
      field: isFleetMaintenanceHubCanada() ? KILOMETERS : MILES,
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: isFleetMaintenanceHubCanada() ? CPK : CPM,
      field: "cost_per_mile",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer,
      sortable: false
    },
    {
      headerName: t("lastMaintLabel"),
      field: "last_maint",
      width: widths.large,
      cellRendererFramework: defaultCellRenderer,
      sortable: false
    }
  ]
  if (vehicles && vehicles.filter((v) => v.fleetcor_enrolled == true).length > 0) {
    fields = [
      ...fields,
      {
        headerName: t("editLabel"),
        field: "edit_button",
        width: widths.verySmall,
        cellRendererFramework: editCellRenderer,
        suppressMenu: true,
        suppressSorting: true,
        sortable: false
      }
    ]
  }
  if (!isFleetcor)
    fields = [
      ...fields,
      {
        headerName: t("deleteLabel"),
        field: "delete_button",
        width: widths.verySmall,
        cellRendererFramework: deleteCellRenderer,
        suppressMenu: true,
        suppressSorting: true,
        sortable: false
      }
    ]
  return fields
}

export function agGridRowData(vehicles, users, language, isFleetcor) {
  return (vehicles || []).map((vehicle) => {
    return {
      vehicle_name: vehicle.vehicle_name,
      plate: vehicleHelpers.formattedLicensePlateNumber(vehicle, true),
      vin: vehicle.vin,
      other_id: vehicle.other_id,
      name_of_vehicle: vehicle.name_of_vehicle,
      department_number: vehicle.department_number,
      license_plate_number: vehicle.license_plate_number,
      driver_name: vehicleHelpers.formattedDriverNames(vehicle.driver_names, language),
      driver_names_list: vehicle.driver_names,
      [isFleetMaintenanceHubCanada() ? KILOMETERS : MILES]: numberHelpers.formatWithCommas(
        isFleetMaintenanceHubCanada() ? vehicle.kilometers : vehicle.miles
      ),
      last_maint: vehicleHelpers.formattedLastMaintenanceDate(vehicle, language),
      cost_per_mile: vehicleHelpers.formattedCostPerMile(vehicle),
      edit_button: vehicle,
      delete_button: vehicle,
      deactivate_button: vehicle,

      // NOTE: The ID column does not get displayed because there is no mapping for it in the columnDefs, however, providing here makes it available to the row's data attribute.
      id: vehicle.id,
      vehicle: vehicle,
      fleet_id: vehicle.fleet_id,
      fleet_name: vehicle.fleet_name,
      bulk_activation: vehicle
    }
  })
}

export function agGridFMCColumnDefs(defaultCellRenderer, t, vehicles, fmc) {
  const widths = {
    verySmall: 125,
    small: 150,
    medium: 175,
    large: 200,
    veryLarge: 225
  }
  var fields = []
  fields = [
    ...fields,
    {
      headerName: t("vehicleLabel"),
      field: "vehicle_name",
      width: widths.veryLarge,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("otherIdLabel"),
      field: "other_id",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("vehicle:vehicleNameLabel"),
      field: "name_of_vehicle",
      width: widths.veryLarge,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("dashboard:DepartmentNumber"),
      field: "department_number",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("vehicle:vinLabel"),
      field: "vin",
      width: widths.veryLarge,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("vehicle:licencePlateNumber"),
      field: "license_plate_number",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer,
      sortable: false
    },
    {
      headerName: t("vehicle:fleetLabel"),
      field: "fleet_name",
      width: widths.veryLarge,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: t("driversLabel"),
      field: "driver_name",
      width: widths.veryLarge,
      cellRendererFramework: driversCellRenderer(defaultCellRenderer)
    },
    {
      headerName: isFleetMaintenanceHubCanada()
        ? t("dashboard:kilometersLabel")
        : t("dashboard:mileageHeadingLabel"),
      field: isFleetMaintenanceHubCanada() ? KILOMETERS : MILES,
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer
    },
    {
      headerName: isFleetMaintenanceHubCanada() ? CPK : CPM,
      field: "cost_per_mile",
      width: widths.medium,
      cellRendererFramework: defaultCellRenderer,
      sortable: false
    }
  ]
  return fields
}

export const KILOMETERS = "kilometers"
export const MILES = "miles"
export const NA = "N/A"
export const FLEET = "Fleet"
export const CUSTOMER = "Customer"

export function handleKeypress(e) {
  var regex = new RegExp("^[a-zA-Z0-9]+$")
  var key = String.fromCharCode(!e.charCode ? e.which : e.charCode)
  if (!regex.test(key)) {
    e.preventDefault()
    return false
  }
}
